// Configuração de moedas e formatação por idioma - baseado na documentação oficial
export interface CurrencyConfig {
  code: string;
  symbol: string;
  position: 'before' | 'after';
  decimalSeparator: string;
  thousandSeparator: string;
  decimals: number;
}

export interface LanguageConfig {
  code: string;
  name: string;
  nativeName: string;
  defaultCurrency: string;
  rtl: boolean;
}

export const LANGUAGES: Record<string, LanguageConfig> = {
  'pt-BR': {
    code: 'pt-BR',
    name: 'Portuguese (Brazil)',
    nativeName: 'Português (Brasil)',
    defaultCurrency: 'BRL',
    rtl: false,
  },
  'pt-PT': {
    code: 'pt-PT',
    name: 'Portuguese (Portugal)',
    nativeName: 'Português (Portugal)',
    defaultCurrency: 'EUR',
    rtl: false,
  },
  'en-US': {
    code: 'en-US',
    name: 'English',
    nativeName: 'English',
    defaultCurrency: 'USD',
    rtl: false,
  },
  'es-ES': {
    code: 'es-ES',
    name: 'Spanish',
    nativeName: 'Español',
    defaultCurrency: 'EUR',
    rtl: false,
  },
  'ja-JP': {
    code: 'ja-JP',
    name: 'Japanese',
    nativeName: '日本語',
    defaultCurrency: 'USD',
    rtl: false,
  },
  'ru-RU': {
    code: 'ru-RU',
    name: 'Russian',
    nativeName: 'Русский',
    defaultCurrency: 'EUR',
    rtl: false,
  },
};

export const CURRENCIES: Record<string, CurrencyConfig> = {
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    position: 'before',
    decimalSeparator: ',',
    thousandSeparator: '.',
    decimals: 2,
  },
  USD: {
    code: 'USD',
    symbol: '$',
    position: 'before',
    decimalSeparator: '.',
    thousandSeparator: ',',
    decimals: 2,
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    position: 'before',
    decimalSeparator: ',',
    thousandSeparator: '.',
    decimals: 2,
  },
};

// Taxas de conversão baseadas na documentação
export const EXCHANGE_RATES = {
  base: 'BRL' as const,
  rates: {
    BRL_USD: 0.184,
    BRL_EUR: 0.169,
    USD_BRL: 5.43,
    EUR_BRL: 5.92,
    USD_EUR: 0.92,
    EUR_USD: 1.09,
  },
  lastUpdate: '2025-09-20',
};

// Produtos e preços por moeda conforme documentação
export const PRODUCT_PRICING = {
  'lead-manager-pro': {
    monthly: { BRL: 800, USD: 147, EUR: 135 },
    annual: { BRL: 8160, USD: 1504, EUR: 1377 },
  },
  'followup-ai': {
    monthly: { BRL: 1200, USD: 221, EUR: 202 },
    annual: { BRL: 12240, USD: 2254, EUR: 2064 },
  },
  'ai-agents': {
    monthly: { BRL: 1500, USD: 276, EUR: 253 },
    annual: { BRL: 15300, USD: 2816, EUR: 2580 },
  },
  'contratos-digitais': {
    monthly: { BRL: 2000, USD: 368, EUR: 337 },
    annual: { BRL: 20400, USD: 3757, EUR: 3440 },
  },
  'lead-insights': {
    monthly: { BRL: 1800, USD: 331, EUR: 303 },
    annual: { BRL: 18360, USD: 3382, EUR: 3096 },
  },
  'site-imobiliario': {
    monthly: { BRL: 3500, USD: 645, EUR: 590 },
    annual: { BRL: 35700, USD: 6574, EUR: 6018 },
  },
  // Bundles com desconto
  'bundle-essencial': {
    monthly: { BRL: 2000, USD: 368, EUR: 337 },
    annual: { BRL: 20400, USD: 3757, EUR: 3440 },
  },
  'bundle-profissional': {
    monthly: { BRL: 3800, USD: 700, EUR: 641 },
    annual: { BRL: 38760, USD: 7140, EUR: 6536 },
  },
  'bundle-premium': {
    monthly: { BRL: 6000, USD: 1105, EUR: 1012 },
    annual: { BRL: 61200, USD: 11271, EUR: 10322 },
  },
};

export function formatCurrency(
  amount: number,
  currencyCode: string = 'BRL',
  locale: string = 'pt-BR'
): string {
  const currency = CURRENCIES[currencyCode];
  if (!currency) return amount.toString();

  // Formatar número com separadores
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: currency.decimals,
    maximumFractionDigits: currency.decimals,
  }).format(amount);

  // Aplicar símbolo da moeda
  return currency.position === 'before'
    ? `${currency.symbol} ${formatted}`
    : `${formatted} ${currency.symbol}`;
}

export function convertCurrency(amount: number, fromCurrency: string, toCurrency: string): number {
  if (fromCurrency === toCurrency) return amount;

  const rateKey = `${fromCurrency}_${toCurrency}` as keyof typeof EXCHANGE_RATES.rates;
  const rate = EXCHANGE_RATES.rates[rateKey];

  if (rate) {
    return Math.round(amount * rate * 100) / 100;
  }

  // Conversão via BRL como base
  const toBrlKey = `${fromCurrency}_BRL` as keyof typeof EXCHANGE_RATES.rates;
  const fromBrlKey = `BRL_${toCurrency}` as keyof typeof EXCHANGE_RATES.rates;

  const toBrlRate = EXCHANGE_RATES.rates[toBrlKey];
  const fromBrlRate = EXCHANGE_RATES.rates[fromBrlKey];

  if (toBrlRate && fromBrlRate) {
    const brlAmount = amount * toBrlRate;
    return Math.round(brlAmount * fromBrlRate * 100) / 100;
  }

  return amount;
}

export function getProductPrice(
  productId: string,
  period: 'monthly' | 'annual',
  currency: string = 'BRL'
): number | null {
  const product = PRODUCT_PRICING[productId as keyof typeof PRODUCT_PRICING];
  if (!product) return null;

  return product[period][currency as keyof (typeof product)[typeof period]] || null;
}

export function getLanguageFromLocale(locale: string): string {
  // Mapear locale para código de idioma simples
  const mapping: Record<string, string> = {
    'pt-BR': 'pt',
    'pt-PT': 'pt-pt',
    'en-US': 'en',
    'es-ES': 'es',
    'ja-JP': 'ja',
    'ru-RU': 'ru',
  };

  return mapping[locale] || 'pt';
}
